const EventNames = require('@/lib/Events/EventNames');
const logFactory = require('@/libraries/DigiTickets/Logging/logFactory');
const OnlineTaskTrigger = require('@/lib/OnlineQueue/OnlineTaskTrigger');
const { findErrorsToDisplayAsWarnings, findNonRecoverableErrors } = require('@/lib/Orders/orderErrorFunctions');

const GeneralResponseInterceptor = function ($rootScope, $q, $injector) {
    const logger = logFactory('ResponseInterceptor');

    let dontLogHttpStatuses = [
        0, // Some client side communication error (offline?)

        // TODO: Likely a validation error but we also throw 400s for missing data which is actually a coding error
        // we want to know about.
        // 400, // Bad Request

        401, // Unauthorized
    ];

    function success(response) {
        // A successful request - let the online notifier know - we must be online
        if (response.config.url.indexOf('/status') === -1 && response.status === 200 && typeof response.data !== 'string') {
            $rootScope.$broadcast('requestSuccess', response);
        }

        return response;
    }

    const shouldLogError = (response) => {
        return dontLogHttpStatuses.indexOf(response.status) === -1;
    };

    const logError = (response) => {
        // Record the error message and send it to Sentry.
        logger.error(
            'API error'
            + ' ' + response.status // HTTP status code
            + ' ' + response.config.method // GET/POST/etc.
            + ' ' + response.config.url // URL of the request
            + (response.data ? ' ' + JSON.stringify(response.data) : ''), // Response from the server
            {
                response,
            }
        );
    };

    /**
     * @param response
     * @return {boolean}
     */
    const shouldDisplayError = (response) => {
        // Don't display errors for queue events running in the background.
        if (
            response.config
            && response.config.data
            && response.config.data.onlineTaskTrigger
            && [
                OnlineTaskTrigger.OFFLINE,
                OnlineTaskTrigger.OFFLINE_RETRY,
                OnlineTaskTrigger.ONLINE_RETRY,
            ].includes(response.config.data.onlineTaskTrigger)
        ) {
            return false;
        }

        // If the user switches company (common in devs).
        if (response.data && response.data.error === 'Invalid tradingSessionID.') {
            return false;
        }

        // Only display 500 errors.
        if (response.status !== 500) {
            return false;
        }

        return true;
    };

    const displayError = (response) => {
        let errorMessage = '';
        if (response.data && response.data.hasOwnProperty('message')) {
            errorMessage = response.data.message;
        } else if (response.data && response.data.hasOwnProperty('error')) {
            errorMessage = response.data.error;
        }

        let toastFactory = $injector.get('toastFactory');

        // Display some errors as a warning instead of an error.
        const warningMessage = findErrorsToDisplayAsWarnings(errorMessage);
        if (warningMessage) {
            toastFactory.warningTop(null, errorMessage);
            return;
        }

        // If we know the error type we can give an improved error experience.
        let message;
        const nonRecoverableMessage = findNonRecoverableErrors(errorMessage);
        if (nonRecoverableMessage) {
            message = '<div class="text-left">';
            message += '<span>' + nonRecoverableMessage + '</span>';
            if (errorMessage && errorMessage !== nonRecoverableMessage) {
                message += ' <a onclick="jQuery(this).prev().hide(); jQuery(this).next().show(); jQuery(this).hide();">[more]</a>';
                message += '<span style="display: none">' + errorMessage + '</span>';
            }
            message += '</div>';
        } else {
            message = (errorMessage ? '<div class="text-left"><small>' + errorMessage + '</small></div>' : '');
        }

        toastFactory.errorTop(
            'ORDER_ERROR.GENERIC_SERVER_ERROR',
            message,
            {
                tapToDismiss: false,
            }
        );
    };

    function error(response) {
        // A failed request - we are probably offline or DT API is in maintenance mode
        if ((response.config.url.indexOf('/status') === -1 && response.status === 0) || response.status === 503) {
            $rootScope.$broadcast('requestError', response);
        }

        if (response.status === 401) {
            // The response object of both Axios and angular-resource error seem to be compatible.
            $rootScope.$broadcast(EventNames.API_REQUEST_UNAUTHORIZED, response);
        }

        if (shouldDisplayError(response)) {
            displayError(response);
        }

        if (shouldLogError(response)) {
            logError(response);
        }

        return $q.reject(response);
    }

    return function (promise) {
        return promise.then(success, error);
    };
};

module.exports = GeneralResponseInterceptor;
